// Almacenamiento offline del módulo Aplicación de Fertilizantes. Base de
// datos IndexedDB independiente de `familygrowers-campo-offline` (la que usa
// el módulo Aplicaciones): la cola pendiente queda completamente separada,
// sin tocar la base ni el DB_VERSION existentes.
//
// El módulo no depende de ningún catálogo (nombre comercial es texto libre
// digitado por el usuario) — no hay store de catálogo aquí.
//
// Mismo patrón que services/offline/offlineDb.js: aislamiento por
// auth_user_id, sin librerías externas.

const DB_NAME = 'familygrowers-campo-fertilizantes-offline';
const DB_VERSION = 1;
const STORE = 'pending_aplicaciones_fertilizantes';

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB no disponible en este navegador.'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'client_operation_id' });
        store.createIndex('by_auth_user_id', 'auth_user_id', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

function promisifyRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function addPendingAplicacionFertilizante(record) {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).put(record);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function listPendingAplicacionesFertilizantesByUser(authUserId) {
  if (!authUserId) return [];
  const db = await openDb();
  const tx = db.transaction(STORE, 'readonly');
  const index = tx.objectStore(STORE).index('by_auth_user_id');
  const results = await promisifyRequest(index.getAll(IDBKeyRange.only(authUserId)));
  return (results || []).sort((a, b) => (a.created_at_local < b.created_at_local ? 1 : -1));
}

export async function updatePendingAplicacionFertilizanteStatus(clientOperationId, changes) {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  const existing = await promisifyRequest(store.get(clientOperationId));
  if (existing) {
    store.put({ ...existing, ...changes });
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function removePendingAplicacionFertilizante(clientOperationId) {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).delete(clientOperationId);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Se llama al cerrar sesión (junto a clearUserOfflineData, el equivalente de
// Aplicaciones): borra solo los registros del usuario que se va.
export async function clearUserOfflineFertilizantesData(authUserId) {
  if (!authUserId) return;
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const index = tx.objectStore(STORE).index('by_auth_user_id');
  const cursorRequest = index.openCursor(IDBKeyRange.only(authUserId));

  await new Promise((resolve, reject) => {
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
    cursorRequest.onerror = () => reject(cursorRequest.error);
  });

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
